import { Suspense, useRef, useState } from 'react'

import {
  AlertCheckIcon,
  AlertErrorIcon,
  AlertInfoIcon,
} from '@/components/icon'
import {
  Button,
  Center,
  Clipboard,
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogContext,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  Field,
  Flex,
  Grid,
  HStack,
  Input,
  QRCode,
  Separator,
  Stack,
  Text,
  toaster,
} from '@/components/ui'
import { ERROR_MESSAGES } from '@/constants/errorMessages'
import { ApiError, useMutation, useQuerySuspense } from '@/lib/api'
import {
  disableTOTP,
  fetchTOTPEnabled,
  setupTOTPSecret,
  useAuthenticatedUser,
  verifyTOTP,
} from '@/lib/auth'

const SetupContent = ({ onSetup }: { onSetup: () => void }) => {
  const { data: secret } = useQuerySuspense('totp-secret', setupTOTPSecret, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
  })
  const user = useAuthenticatedUser()

  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState<string | null>(null)
  const [apiError, setApiError] = useState<ApiError>()

  const { request: submitVerifyTOTP, isPending } = useMutation(verifyTOTP, {
    onSuccess: () => {
      toaster.create({
        type: 'success',
        title: '2段階認証を設定しました',
      })
      onSetup()
    },
    onError: error => {
      if (error.message === ERROR_MESSAGES.codeMismatch) {
        setApiError(new ApiError(ERROR_MESSAGES.codeMismatchSetup))
      } else {
        setApiError(error)
      }
    },
    cacheKeysOnSuccess: ['totp-enabled'],
  })

  return (
    <>
      <DialogHeader p="6">
        <DialogTitle>2段階認証設定</DialogTitle>
      </DialogHeader>
      <Separator />
      <DialogBody p="6">
        <Stack gap="4">
          <Text>
            Google
            Authenticatorなどの認証コード生成用のモバイルアプリで二次元コードを読み取るか、シークレットコードを手動で入力し、表示される認証コードを入力してください。
          </Text>
          <Stack gap="0.5">
            <HStack as="dl">
              <Text as="dt" wordBreak="keep-all">
                シークレットコード:
              </Text>
              <Text as="dd" wordBreak="break-all">
                {secret}
              </Text>
              <Clipboard value={secret} />
            </HStack>
            <HStack as="dl">
              <Text as="dt" wordBreak="keep-all">
                アカウントID:
              </Text>
              <Text as="dd" wordBreak="break-all">
                {user.generalUser.email}
              </Text>
              <Clipboard value={user.generalUser.email} />
            </HStack>
          </Stack>
          <Center>
            <QRCode
              value={`otpauth://totp/MiROHA-${user.generalUser.email}?secret=${secret}`}
              size="lg"
            />
          </Center>
          <Flex justify="center">
            <Field
              label="認証コード"
              maxW="440px"
              invalid={!!codeError}
              errorText={codeError}
            >
              <Input
                type="tel"
                value={code}
                onChange={e => {
                  const value = e.target.value
                  setCode(value)
                  setCodeError(
                    value !== '' ? null : '認証コードを入力してください',
                  )
                }}
                autoComplete="one-time-code"
                placeholder="認証コードを入力してください"
              />
            </Field>
          </Flex>
          {!!apiError && (
            <HStack gap="3" justify="center" color="text.error">
              <AlertErrorIcon />
              <Text fontWeight="normal">{apiError.message}</Text>
            </HStack>
          )}
        </Stack>
      </DialogBody>
      <Separator />
      <DialogFooter p="6">
        <DialogActionTrigger asChild>
          <Button variant="solid" colorPalette="gray">
            キャンセル
          </Button>
        </DialogActionTrigger>
        <Button
          disabled={!code}
          loading={isPending}
          onClick={async () => {
            await submitVerifyTOTP({
              code,
            })
          }}
        >
          認証
        </Button>
      </DialogFooter>
    </>
  )
}

const ConfirmedContent = () => {
  return (
    <>
      <DialogHeader p="6">
        <DialogTitle>2段階認証設定</DialogTitle>
      </DialogHeader>
      <Separator />
      <DialogBody>
        <Grid
          templateColumns="max-content 1fr"
          px="4"
          py="3"
          columnGap="3"
          rowGap="8"
          color="text.primary"
          lineHeight="24px"
        >
          <AlertInfoIcon size="24px" />
          <Stack gap="1">
            <Text fontWeight="bold">
              MiROHAをお使いの端末を、これから機種変更される場合
            </Text>
            <Text color="text.main">
              機種変更前にMiROHAの2段階認証設定を解除の上、再度新しい端末での2段階認証の設定をお願いします。
            </Text>
          </Stack>

          <AlertInfoIcon size="24px" />
          <Stack gap="1">
            <Text fontWeight="bold">
              2段階認証の解除をせず、使用している端末やアプリを紛失された場合
            </Text>
            <Text color="text.main">
              ログインができなくなります。MiROHAの運用デスクへお問い合わせください。
            </Text>
          </Stack>
        </Grid>
      </DialogBody>
      <Separator />
      <DialogFooter p="6">
        <DialogActionTrigger asChild>
          <Button variant="outline">閉じる</Button>
        </DialogActionTrigger>
      </DialogFooter>
    </>
  )
}

const DisableContent = ({ onDisable }: { onDisable: () => void }) => {
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState<string | null>(null)
  const [apiError, setApiError] = useState<ApiError>()
  const { request: submitDisableTOTP, isPending } = useMutation(disableTOTP, {
    onSuccess: () => {
      toaster.create({
        type: 'success',
        title: '2段階認証を解除しました。',
      })
      onDisable()
    },
    onError: error => {
      if (error.message === ERROR_MESSAGES.codeMismatch) {
        setApiError(new ApiError(ERROR_MESSAGES.codeMismatchDisable))
      } else {
        setApiError(error)
      }
    },
    cacheKeysOnSuccess: ['totp-enabled'],
  })
  return (
    <>
      <DialogHeader p="6">
        <DialogTitle>2段階認証設定</DialogTitle>
      </DialogHeader>
      <Separator />
      <DialogBody p="6">
        <Stack gap="4">
          <Grid
            templateColumns="max-content 1fr"
            px="4"
            py="3"
            columnGap="3"
            color="text.success"
            lineHeight="24px"
          >
            <AlertCheckIcon size="24px" />
            <Stack gap="1">
              <Text fontWeight="bold">2段階認証が設定されています</Text>
              <Text color="text.main">
                解除する場合は認証用モバイルアプリに表示されている認証コードを入力してください。
              </Text>
            </Stack>
          </Grid>
          <Flex justify="center">
            <Field
              label="認証コード"
              maxW="440px"
              errorText={codeError}
              invalid={!!codeError}
            >
              <Input
                type="tel"
                value={code}
                onChange={e => {
                  const value = e.target.value
                  setCode(value)
                  setCodeError(
                    value !== '' ? null : '認証コードを入力してください',
                  )
                }}
                autoComplete="one-time-code"
                placeholder="認証コードを入力してください"
              />
            </Field>
          </Flex>
          {!!apiError && (
            <HStack gap="3" justify="center" color="text.error">
              <AlertErrorIcon />
              <Text fontWeight="normal">{apiError.message}</Text>
            </HStack>
          )}
        </Stack>
      </DialogBody>
      <Separator />
      <DialogFooter p="6">
        <DialogActionTrigger asChild>
          <Button variant="solid" colorPalette="gray">
            キャンセル
          </Button>
        </DialogActionTrigger>
        <Button
          disabled={!code}
          loading={isPending}
          onClick={async () => {
            await submitDisableTOTP({
              code,
            })
          }}
        >
          解除する
        </Button>
      </DialogFooter>
    </>
  )
}

const Content = ({ onClose }: { onClose: () => void }) => {
  const { data: isTOTPEnabled } = useQuerySuspense(
    'totp-enabled',
    fetchTOTPEnabled,
  )
  // ダイアログを開いた時点でTOTPが有効かどうかチェック
  const firstEnabledRef = useRef(isTOTPEnabled)

  const [isJustSetup, setIsJustSetup] = useState(false)

  if (firstEnabledRef.current) {
    return <DisableContent onDisable={onClose} />
  }

  if (isJustSetup) {
    return <ConfirmedContent />
  }

  return (
    <SetupContent
      onSetup={() => {
        setIsJustSetup(true)
      }}
    />
  )
}

type Props = {
  trigger: React.ReactNode
}
export const MFADialog = ({ trigger }: Props) => {
  return (
    <DialogRoot size="xl">
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogContext>
          {({ open, setOpen }) =>
            open && (
              <Suspense fallback={null}>
                <Content
                  onClose={() => {
                    setOpen(false)
                  }}
                />
                <DialogCloseTrigger />
              </Suspense>
            )
          }
        </DialogContext>
      </DialogContent>
    </DialogRoot>
  )
}
